import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, MapPin, Users, BookOpen, Info, Calendar, Flame, ShoppingBag, Video, UserPlus } from 'lucide-react';
import { Button } from '../ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
interface SubLink {
  name: string;
  path: string;
  external?: boolean;
  icon?: React.ReactNode;
  description?: string;
}
interface NavLink {
  name: string;
  path: string;
  subLinks?: SubLink[];
}
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileSubMenuOpen, setMobileSubMenuOpen] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsOpen(false);
    setOpenDropdown(null);
    setMobileSubMenuOpen(null);
  }, [location]);
  const navLinks: NavLink[] = [{
    name: 'HOME',
    path: '/'
  }, {
    name: 'ABOUT',
    path: '/about',
    subLinks: [{
      name: 'Our Story',
      path: '/about',
      description: 'Learn about our journey',
      icon: <Info className="w-4 h-4" data-id="element-165" />
    }, {
      name: 'Plan Your Visit',
      path: '/visit',
      description: 'What to expect',
      icon: <MapPin className="w-4 h-4" data-id="element-166" />
    }]
  }, {
    name: 'MINISTRIES',
    path: '/ministries',
    subLinks: [{
      name: 'Our Ministries',
      path: '/ministries',
      description: 'Get involved',
      icon: <Users className="w-4 h-4" data-id="element-167" />
    }, {
      name: 'What We Believe',
      path: '/what-we-believe',
      description: 'Our faith statement',
      icon: <BookOpen className="w-4 h-4" data-id="element-168" />
    }, {
      name: 'Join the Team',
      path: '/join-team',
      description: 'Serve with us',
      icon: <UserPlus className="w-4 h-4" data-id="element-169" />
    }]
  }, {
    name: 'SERMONS',
    path: '/resources',
    subLinks: [{
      name: 'Resources Archive',
      path: '/resources',
      description: 'Sermons, media & materials',
      icon: <BookOpen className="w-4 h-4" data-id="element-170" />
    }, {
      name: 'Watch Live',
      path: '/live',
      description: 'Join our services online',
      icon: <Video className="w-4 h-4" data-id="element-171" />
    }]
  }, {
    name: 'STORE',
    path: '/store'
  }, {
    name: 'EVENTS',
    path: '/events',
    subLinks: [{
      name: 'All Events & Services',
      path: '/events',
      description: 'Daily, Weekly & Monthly',
      icon: <Calendar className="w-4 h-4" data-id="element-172" />
    }, {
      name: 'Deep Dive Bible Study',
      path: '/deep-dive',
      description: 'Lessons, materials & assessments',
      icon: <Flame className="w-4 h-4" data-id="element-173" />
    }]
  }, {
    name: 'GIVE',
    path: '/give'
  }, {
    name: 'CONTACT',
    path: '/contact'
  }];
  const isSubLinkActive = (link: NavLink) => {
    if (!link.subLinks) return false;
    return link.subLinks.some(sub => !sub.external && location.pathname === sub.path);
  };
  return <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A1F44] shadow-lg py-2' : 'bg-transparent py-6'}`} data-id="element-174">
      <div className="container-custom" data-id="element-175">
        <div className="flex flex-wrap items-center justify-between" data-id="element-176">
          {/* Logo */}
          <Link to="/" className={`flex items-center gap-1 md:gap-2 group z-10 transition-all duration-300 min-w-0 flex-shrink ${scrolled ? 'scale-90 origin-left' : ''}`} data-id="element-177">
            <img src="https://cdn.magicpatterns.com/uploads/kgPfyhKNzmQ6jDdg2wxmuj/NEW_HOCC_LOGO_-_WEBSITE.png" alt="TheHousehold Logo" className="w-10 h-10 md:w-14 md:h-14 rounded-full shrink-0 shadow-lg object-cover" data-id="element-178" />
            <div className="flex flex-col min-w-0" data-id="element-179">
              <div className="text-white font-montserrat text-sm sm:text-lg md:text-xl font-base tracking-wide leading-tight truncate flex items-baseline gap-1.5" data-id="element-180">
                <span data-id="element-181">HOUSEHOLD</span>
                <span className="text-[0.7em] font-normal opacity-90" data-id="element-182">OF</span>
                <span data-id="element-183">CHRIST CHURCH</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-[10px] sm:text-xs font-montserrat tracking-[0.2em] uppercase mt-0.5 md:mt-1" data-id="element-184">
                <div className="h-[1px] w-3 md:w-4 bg-white" data-id="element-185"></div>
                <span className="truncate text-[#fc0719] font-bold" data-id="element-186">
                  THEHOUSEHOLD
                </span>
                <div className="h-[1px] w-3 md:w-4 bg-white" data-id="element-187"></div>
                <span className="hidden sm:inline" data-id="element-188">GATEWAY OF HEAVEN</span>
              </div>
            </div>
          </Link>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2 text-white z-10 shrink-0 ml-auto" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu" data-id="element-189">
            {isOpen ? <X className="w-6 h-6" data-id="element-190" /> : <Menu className="w-6 h-6" data-id="element-191" />}
          </button>

          {/* Desktop Navigation Links */}
          <div className={`hidden md:flex items-center justify-center gap-6 lg:gap-10 transition-all duration-300 ${scrolled ? 'order-none flex-1 px-4 xl:px-8' : 'order-last w-full mt-6'}`} data-id="element-192">
            {navLinks.map(link => link.subLinks ? <div key={link.name} className="relative" onMouseEnter={() => setOpenDropdown(link.name)} onMouseLeave={() => setOpenDropdown(null)} data-id="element-193">
                  <button className={`text-sm font-bold tracking-widest uppercase transition-colors hover:text-white/80 flex items-center gap-1.5 py-2 ${isSubLinkActive(link) ? 'text-white' : 'text-white/90'}`} data-id="element-194">
                    {link.name}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openDropdown === link.name ? 'rotate-180' : ''}`} data-id="element-195" />
                    {isSubLinkActive(link) && <motion.div layoutId="nav-indicator" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#fc0719]" data-id="element-196" />}
                  </button>

                  <AnimatePresence data-id="element-197">
                    {openDropdown === link.name && <motion.div initial={{
                opacity: 0,
                y: 8,
                x: '-50%'
              }} animate={{
                opacity: 1,
                y: 0,
                x: '-50%'
              }} exit={{
                opacity: 0,
                y: 8,
                x: '-50%'
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute top-full left-1/2 pt-3 z-50" style={{
                width: 'max-content'
              }} data-id="element-198">
                        <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden min-w-[320px]" data-id="element-199">
                          <div className="h-1 bg-[#fc0719]" data-id="element-200" />
                          <div className="p-2" data-id="element-201">
                            {link.subLinks.map(subLink => <Link key={subLink.name} to={subLink.path} className={`flex items-start gap-3 px-4 py-3 rounded-lg transition-all hover:bg-[#F5F7FA] group ${location.pathname === subLink.path ? 'bg-[#F5F7FA]' : ''}`} data-id="element-202">
                                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-colors ${location.pathname === subLink.path ? 'bg-[#0A1F44] text-white' : 'bg-[#F5F7FA] text-[#0A1F44] group-hover:bg-[#0A1F44] group-hover:text-white'}`} data-id="element-203">
                                  {subLink.icon}
                                </span>
                                <div data-id="element-204">
                                  <span className={`block font-bold whitespace-nowrap transition-colors ${location.pathname === subLink.path ? 'text-[#fc0719]' : 'text-[#0A1F44] group-hover:text-[#fc0719]'}`} data-id="element-205">
                                    {subLink.name}
                                  </span>
                                  {subLink.description && <span className="block text-xs text-gray-500 mt-0.5" data-id="element-206">
                                      {subLink.description}
                                    </span>}
                                </div>
                              </Link>)}
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div> : <Link key={link.name} to={link.path} className={`text-sm font-bold tracking-widest uppercase transition-colors hover:text-white/80 py-2 relative text-white flex items-center gap-2`} data-id="element-207">
                  {link.name === 'STORE' && <ShoppingBag className="w-4 h-4 text-[#fc0719]" data-id="element-208" />}
                  {link.name}
                  {location.pathname === link.path && <motion.div layoutId="nav-indicator" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#fc0719]" data-id="element-209" />}
                </Link>)}
          </div>

          {/* Desktop CTA */}
          <div className={`hidden md:block transition-all duration-300 z-10 ${scrolled ? 'order-none' : 'ml-auto'}`} data-id="element-210">
            <Link to="/visit" data-id="element-211">
              <Button variant="primary" size={scrolled ? 'sm' : 'md'} className="font-bold tracking-wider text-sm shadow-lg shadow-red-900/20" data-id="element-212">
                JOIN US THIS SUNDAY
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-213">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-[#0A1F44] border-t border-white/10 shadow-xl" data-id="element-214">
            <div className="container-custom py-6 flex flex-col gap-1" data-id="element-215">
              {navLinks.map(link => link.subLinks ? <div key={link.name} data-id="element-216">
                    <button onClick={() => setMobileSubMenuOpen(mobileSubMenuOpen === link.name ? null : link.name)} className={`w-full text-sm tracking-widest font-bold px-4 py-3 rounded-lg flex items-center justify-between transition-colors ${isSubLinkActive(link) || mobileSubMenuOpen === link.name ? 'bg-white/10 text-white border-l-2 border-[#fc0719]' : 'text-white hover:bg-white/5'}`} data-id="element-217">
                      {link.name}
                      <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${mobileSubMenuOpen === link.name ? 'rotate-180' : ''}`} data-id="element-218" />
                    </button>
                    <AnimatePresence data-id="element-219">
                      {mobileSubMenuOpen === link.name && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-220">
                          <div className="py-2 pl-4 space-y-1" data-id="element-221">
                            {link.subLinks.map(subLink => <Link key={subLink.name} to={subLink.path} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === subLink.path ? 'bg-white/10 text-white border-l-2 border-[#fc0719]' : 'text-white/80 hover:bg-white/5 hover:text-white'}`} data-id="element-222">
                                <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/10 text-white" data-id="element-223">
                                  {subLink.icon}
                                </span>
                                <span className="block font-bold text-sm" data-id="element-224">
                                  {subLink.name}
                                </span>
                              </Link>)}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div> : <Link key={link.name} to={link.path} className={`text-sm tracking-widest font-bold px-4 py-3 rounded-lg flex items-center gap-2 transition-colors ${location.pathname === link.path ? 'bg-white/10 text-white border-l-2 border-[#fc0719]' : 'text-white hover:bg-white/5'}`} data-id="element-225">
                    {link.name}
                  </Link>)}
              <div className="pt-6 mt-4 border-t border-white/10" data-id="element-226">
                <Link to="/visit" data-id="element-227">
                  <Button className="w-full" variant="primary" data-id="element-228">
                    JOIN US THIS SUNDAY
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}